import { createSelector } from '@reduxjs/toolkit';
import { SetupIntent, Token } from '@stripe/stripe-js';
import React, { useContext } from 'react';
import Stripe from 'stripe';

import PaymentsClient from 'src/clients/PaymentsClient';
import {
  BankVerifyFormModel,
  resolveMicrodepositType,
} from 'src/constants/componentTypes';
import { PortalConfigContext, useFeatureFlags } from 'src/context';
import { useInvoiceRecipient } from 'src/features/payments/hooks/useInvoiceRecipient';
import { useAppDispatch, useAppSelector } from 'src/hooks/useStore';
import { FormModal } from 'src/legacy/components/Modals';
import { useAddCreditCard } from 'src/routes/payments/payments-methods/hooks/useAddCreditCard';
// Re-export PaymentMethodType with empty string for initial state
export type PaymentMethodType = 'credit_card' | 'plaid' | 'bank' | '';
import {
  useCreateBankSetupIntentMutation,
  useCreateUnverifiedBankAccountMutation,
  useDeletePaymentMethodMutation,
  useDeleteUnverifiedBankAccountMutation,
  useGetCustomerBankAccountsQuery,
  useGetCustomerPaymentMethodsQuery,
  useGetStripeCustomerQuery,
  useGetUnverifiedBankAccountsQuery,
  useSetDefaultPaymentMethodMutation,
  useVerifyUnverifiedBankAccountMutation,
} from 'src/services/api';
import { PaymentOptions, RecipientType } from 'src/services/api/invoicesApi';
import { AddCard, updateSource } from 'src/store/payments/actions';
import { RootState } from 'src/store/reduxTypes';
import { alertSnackbar } from 'src/store/ui/actions';
import { StripePaymentMethod } from 'src/types/payment';
import { arrayToMap } from 'src/utils/array';
import { useAddBankForm } from 'src/routes/payments/payments-methods/hooks/useAddBankForm';
import { AddPaymentMethodModal } from 'src/routes/payments/payments-methods/components/AddPaymentMethodModal';

// Stable empty array to prevent re-renders when feature flag is off
const EMPTY_UNVERIFIED_BANK_ACCOUNTS: never[] = [];

export const defaultPaymentOptions: PaymentOptions = {
  allowPaymentViaACH: true,
  allowPaymentViaCC: true,
  absorbACHFees: false,
  absorbCreditCardFee: false,
  allowPlaidSetup: true,
};

export const initialValues = {
  sourceId: '',
  isPrimary: false,
  disabledCheckPrimary: false,
};

export const initialCardFormValues = {
  ...initialValues,
  name: '',
  postalCode: '',
};

export const initialBankFormValues = {
  ...initialValues,
  accountHolderType: 'individual',
  accountNumber: '',
  accountHolderName: '',
  routingNumber: '',
};

interface UsePaymentMethodsOptions {
  recipientId: string;
  paymentOptions?: PaymentOptions;
  amountDue?: number;
  currency?: string;
  companyId?: string;
}

export type HandleSaveValues = {
  sourceId?: string;
  isPrimary: boolean;
  name?: string;
  postalCode?: string;
  fields?: {
    token?: Token;
    setupIntent?: SetupIntent;
  };
};

const selector = createSelector(
  [
    (state: RootState) => state.user.id,
    (state: RootState) => state.user.instance?.attributes.email,
    (state: RootState) => state.user.data?.portalId,
    (state: RootState) => state.clients.activeClients,
    (state: RootState) => state.activeCompany,
  ],
  (userId, currentUserEmail, portalId, activeClients, activeCompany) => ({
    userId,
    currentUserEmail,
    portalId,
    activeClients: arrayToMap(activeClients, 'id'),
    activeCompanyId: activeCompany.activeCompanyId ?? '',
  }),
);

type InitialValuesType = {
  sourceId: string;
  isPrimary: boolean;
  disabledCheckPrimary: boolean;
};

export type SetEditModalData = {
  values: InitialValuesType;
  paymentMethodType: PaymentMethodType;
  addPaymentMethodFormOpen: boolean;
};

export const usePaymentMethods = (options: UsePaymentMethodsOptions) => {
  const { recipientId, paymentOptions = defaultPaymentOptions } = options;
  const { EnableMicrodepositVerification: enableMicrodepositVerification } =
    useFeatureFlags();
  const invoiceRecipient = useInvoiceRecipient(
    recipientId,
    options?.companyId || '',
  );
  const isRecipientACompany = invoiceRecipient?.type === RecipientType.Company;
  const portalConfig = useContext(PortalConfigContext);
  const portalId = portalConfig.portalHeader;
  const { userId, currentUserEmail, activeClients, activeCompanyId } =
    useAppSelector(selector);
  const companyId =
    options?.companyId ?? (isRecipientACompany ? recipientId : activeCompanyId);

  const { data: stripeCustomer, refetch: loadCustomer } =
    useGetStripeCustomerQuery(
      {
        companyId,
      },
      {
        refetchOnMountOrArgChange: true,
        skip: !companyId,
      },
    );
  const stripeCustomerId = stripeCustomer?.id || '';
  // TODO(@Monfernape): refactor, seems like we can get rid of this
  const {
    data: paymentMethods = [],
    isLoading: isPaymentMethodLoading,
    refetch: reloadPaymentMethods,
  } = useGetCustomerPaymentMethodsQuery(stripeCustomerId, {
    refetchOnMountOrArgChange: true,
  });
  // TODO(@Monfernape): refactor, seems like we can get rid of this
  const {
    data: bankAccountsData,
    isLoading: isBankAccountsLoading,
    refetch: loadBankAccounts,
  } = useGetCustomerBankAccountsQuery(stripeCustomerId, {
    skip: !stripeCustomerId,
    refetchOnMountOrArgChange: true,
  });

  const legacyBankAccounts = bankAccountsData || [];

  // Fetch unverified bank accounts (awaiting microdeposit verification)
  // Only fetch if the feature flag is enabled
  const {
    data: unverifiedBankAccounts = [],
    refetch: loadUnverifiedBankAccounts,
  } = useGetUnverifiedBankAccountsQuery(companyId, {
    skip: !companyId || !enableMicrodepositVerification,
    refetchOnMountOrArgChange: true,
  });

  const [setDefaultPaymentMethod] = useSetDefaultPaymentMethodMutation();
  const [deletePaymentMethod] = useDeletePaymentMethodMutation();
  const [createBankSetupIntent] = useCreateBankSetupIntentMutation();
  const [createUnverifiedBankAccount] =
    useCreateUnverifiedBankAccountMutation();
  const [verifyUnverifiedBankAccount] =
    useVerifyUnverifiedBankAccountMutation();
  const [deleteUnverifiedBankAccount] =
    useDeleteUnverifiedBankAccountMutation();
  const dispatch = useAppDispatch();
  const [addPaymentModalOpen, setAddPaymentModalOpen] = React.useState(false);
  const [sources, setSources] = React.useState<StripePaymentMethod[]>([]);
  const [currentUpdatingSourceId, setCurrentUpdatingSourceId] =
    React.useState('');
  const [showBankAddedModal, setShowBankAddedModal] =
    React.useState<Stripe.BankAccount | null>(null);
  const [bankSetupIntentClientSecret, setBankSetupIntentClientSecret] =
    React.useState<string | undefined>(undefined);
  const [editModalData, setEditModalData] = React.useState({
    values: initialValues,
    paymentMethodType: '',
    addPaymentMethodFormOpen: false,
  });

  // Only refetch queries that are actually started. RTK Query v2 throws
  // "Cannot refetch a query that has not been started yet." when refetch() is
  // called on a skipped query, so each call must mirror its query's skip
  // condition — otherwise the throw aborts callers (e.g. the add-card form
  // would stay open even though the card was saved).
  const refreshPaymentData = async () => {
    if (companyId) {
      await loadCustomer();
    }
    await reloadPaymentMethods();
    if (stripeCustomerId) {
      await loadBankAccounts();
    }
    if (companyId && enableMicrodepositVerification) {
      await loadUnverifiedBankAccounts();
    }
  };

  const recipient = React.useMemo(() => {
    if (isRecipientACompany) {
      const companyClientUser = Array.from(activeClients.values()).find(
        (c) =>
          c.fields.companyId === recipientId ||
          c.fields.companyIds?.includes(recipientId),
      );
      if (companyClientUser) {
        return companyClientUser;
      }
    } else {
      const fetchedClient = activeClients.get(recipientId);
      if (fetchedClient) {
        return fetchedClient;
      }
    }
    return null;
  }, [recipientId, activeClients, isRecipientACompany]);

  const defaultPaymentMethod = React.useMemo(() => {
    if (!stripeCustomer) {
      return '';
    }

    // Stripe keeps default payment source and default payment method in separate fields
    // for backwards compatibility between the legacy Sources API and the PaymentMethod API:
    // - customer.invoice_settings.default_payment_method (PaymentMethod API)
    // - customer.default_source (legacy Sources API)
    // Either field can be a string ID or an expanded object depending on API expand params.
    const dpm = stripeCustomer.invoice_settings.default_payment_method;
    if (dpm) {
      return typeof dpm === 'string' ? dpm : dpm.id;
    }

    const ds = stripeCustomer.default_source;
    if (ds) {
      return typeof ds === 'string' ? ds : ds.id;
    }

    return '';
  }, [stripeCustomer]);

  /**
   * if user edits existing source, update source
   * if user creates a new source, add source to the customer
   * @param values the fields of add/edit payment method form modal
   */
  const handleSave = async (values: HandleSaveValues) => {
    if (values.sourceId) {
      setCurrentUpdatingSourceId(values.sourceId);
      dispatch(
        updateSource(
          stripeCustomerId,
          values.sourceId,
          {
            name: values.name,
            address_zip: values.postalCode,
          },
          values.isPrimary,
        ),
      );
      setCurrentUpdatingSourceId('');
    } else if (values.fields?.setupIntent) {
      // SetupIntent confirmed - bank account pending microdeposit verification
      // Note: us_bank_account PaymentMethods cannot be attached to a customer
      // until they are verified via microdeposits. The PaymentMethod will be
      // stored in pending_bank_accounts table and attached via webhook after verification.
      const paymentMethodId =
        values.fields.setupIntent.payment_method &&
        (typeof values.fields.setupIntent.payment_method === 'string'
          ? values.fields.setupIntent.payment_method
          : values.fields.setupIntent.payment_method.id);
      const setupIntentId = values.fields.setupIntent.id;

      // Store the unverified bank account in the database for display
      if (companyId && paymentMethodId && setupIntentId && paymentMethodId) {
        try {
          await createUnverifiedBankAccount({
            companyId,
            setupIntentId,
            paymentMethodId,
          }).unwrap();
        } catch (err) {
          console.error('Failed to create unverified bank account:', err);
        }
      }

      if (editModalData.paymentMethodType === 'bank') {
        // Create a minimal bank account object for the modal
        // The actual bank account details will be fetched on refresh
        setShowBankAddedModal({
          id: paymentMethodId,
          object: 'bank_account',
          status: 'new',
        } as Stripe.BankAccount);
      }
      // Clear the client secret, then close the modal after successful setup
      setBankSetupIntentClientSecret(undefined);
      setEditModalData({
        values: initialValues,
        addPaymentMethodFormOpen: false,
        paymentMethodType: '',
      });
    } else {
      // Credit card flow: create PaymentMethod from token
      const email = recipient ? recipient?.fields.email : currentUserEmail;

      await dispatch(
        AddCard(
          recipient?.id || userId,
          email || '',
          values.fields?.token?.id ?? '',
          stripeCustomerId,
          values.isPrimary,
          portalId,
          companyId,
        ),
      );
      setEditModalData({
        values: initialValues,
        addPaymentMethodFormOpen: false,
        paymentMethodType: '',
      });
    }

    await refreshPaymentData();
  };

  /**
   * Verify microdeposit amounts for a bank account.
   * Automatically detects whether it's a legacy bank account or new unverified bank account.
   */
  const handleVerify = async (values: BankVerifyFormModel) => {
    const isUnverifiedBankAccount = unverifiedBankAccounts.some(
      (account) => account.id === values.id,
    );

    if (isUnverifiedBankAccount) {
      // New SetupIntent-based flow
      if (
        resolveMicrodepositType(values.microdepositType) === 'descriptor_code'
      ) {
        await verifyUnverifiedBankAccount({
          pendingBankAccountId: values.id,
          descriptorCode: values.descriptorCode,
        }).unwrap();
      } else {
        await verifyUnverifiedBankAccount({
          pendingBankAccountId: values.id,
          amounts: [Number(values.fromAmount), Number(values.toAmount)],
        }).unwrap();
      }

      await refreshPaymentData();
    } else {
      // Legacy bank account flow
      const verifyResult = await PaymentsClient.verifyBank(
        stripeCustomerId,
        values.id,
        {
          amounts: [Number(values.fromAmount), Number(values.toAmount)],
        },
      );
      if (verifyResult && verifyResult.data) {
        setSources(
          sources
            .filter((sourceItem) => sourceItem.id !== verifyResult.data.id)
            .concat([verifyResult.data]),
        );
        loadBankAccounts();
      } else {
        throw new Error('Invalid deposite numbers. Cannot be verified');
      }
    }

    dispatch(alertSnackbar({ successMessage: 'Bank account verified' }));
  };

  /**
   * Update only isPrimary field of the current payment method to be true, while using the other fields as it is.
   * To update isPrimary field of the current source, use the same function as the one to create/edit payment method
   * @param source card information.
   */
  const makePrimaryMethod = async (source: StripePaymentMethod) => {
    await setDefaultPaymentMethod({
      defaultPaymentMethod: source.id,
      companyId,
    });
  };

  /**
   * Delete payment method.
   * Automatically detects whether it's a regular payment method or an unverified bank account.
   * @param sourceId
   */
  const handleDelete = async (sourceId: string) => {
    const isUnverifiedBankAccount = unverifiedBankAccounts.some(
      (account) => account.id === sourceId,
    );

    if (isUnverifiedBankAccount) {
      await deleteUnverifiedBankAccount(sourceId).unwrap();
      loadUnverifiedBankAccounts();
    } else {
      await deletePaymentMethod({
        paymentMethodId: sourceId,
        companyId,
      });
    }
  };

  const handleClose = () => {
    setEditModalData({
      values: initialValues,
      addPaymentMethodFormOpen: false,
      paymentMethodType: '',
    });
    // Clear any pending SetupIntent client secret
    setBankSetupIntentClientSecret(undefined);
  };

  const openAddPaymentModal = () => {
    setAddPaymentModalOpen(true);
  };

  const closeAddPaymentModal = () => {
    setAddPaymentModalOpen(false);
  };

  const handleAddPaymentMethodSelected = async (
    paymentMethodType: PaymentMethodType,
  ) => {
    // set the existing payment methods to determine if the newly added
    // payment should be the primary one.
    const existingPaymentMethods = [
      ...(paymentMethods || []),
      ...legacyBankAccounts,
    ];

    // For bank accounts with microdeposit verification enabled,
    // fetch SetupIntent client secret first. SetupIntent uses on_behalf_of
    // so microdeposits show the portal's business name
    if (
      paymentMethodType === 'bank' &&
      companyId &&
      enableMicrodepositVerification
    ) {
      const setupIntent = await createBankSetupIntent().unwrap();
      setBankSetupIntentClientSecret(setupIntent.clientSecret);
    }

    setEditModalData({
      addPaymentMethodFormOpen: true,
      paymentMethodType,
      values: {
        ...(paymentMethodType === 'bank'
          ? initialBankFormValues
          : initialCardFormValues),
        isPrimary:
          paymentMethodType === 'bank'
            ? existingPaymentMethods.length === 0
            : initialCardFormValues.isPrimary,
        disabledCheckPrimary:
          paymentMethods.length === 0 ||
          defaultPaymentMethod === currentUpdatingSourceId,
      },
    });
    setAddPaymentModalOpen(false);
  };

  /**
   * Renders the payment method modals.
   * @param formOnly - When true, only renders the add/edit form modal, not the payment type selector.
   *                   Use this in contexts where the payment type selector is shown separately.
   */
  const modalRenderer = ({ formOnly }: { formOnly?: boolean } = {}) => {
    const { addPaymentMethodFormOpen, values, paymentMethodType } =
      editModalData;

    const sourceId = values.sourceId || '';
    const paymentMethodDetails = paymentMethods.find(
      (method) => method.id === sourceId,
    );
    const isDefaultPaymentMethod =
      paymentMethodDetails?.id === defaultPaymentMethod;

    const formValues = sourceId
      ? {
          ...values,
          name: paymentMethodDetails?.billing_details.name || '',
          postalCode:
            paymentMethodDetails?.billing_details.address?.postal_code || '',
          cardEnding: paymentMethodDetails?.card?.last4 || '',
          brand: paymentMethodDetails?.card?.brand || '',
          isPrimary: isDefaultPaymentMethod || false,
          expMonth: paymentMethodDetails?.card?.exp_month || '',
          expYear: paymentMethodDetails?.card?.exp_year || '',
          isDefaultPaymentMethod: isDefaultPaymentMethod,
        }
      : values;

    if (addPaymentMethodFormOpen) {
      const formModalOptions = {
        title: '',
        initialFormValue: formValues,
        buttonLabel: values.sourceId ? 'Update' : 'Add',
        handleSave,
        onClose: handleClose,
        open: addPaymentMethodFormOpen,
      };

      const titlePrefix = values.sourceId ? 'Update' : 'Add';
      formModalOptions.title =
        paymentMethodType === 'credit_card'
          ? `${titlePrefix} card`
          : `${titlePrefix} bank account`;

      return (
        <FormModal
          {...formModalOptions}
          useFormHook={() =>
            paymentMethodType === 'credit_card'
              ? useAddCreditCard({ paymentMethods })
              : useAddBankForm({
                  clientSecret: bankSetupIntentClientSecret,
                  enableMicrodepositVerification:
                    enableMicrodepositVerification ?? false,
                  email: recipient
                    ? recipient.fields.email
                    : currentUserEmail || undefined,
                })
          }
        />
      );
    }

    if (!formOnly && addPaymentModalOpen) {
      return (
        <AddPaymentMethodModal
          modalProps={{
            open: addPaymentModalOpen,
            onClose: closeAddPaymentModal,
          }}
          paymentOptions={paymentOptions}
          onPaymentMethodSelected={handleAddPaymentMethodSelected}
          amountDue={options.amountDue || 0}
          currency={options.currency || ''}
        />
      );
    }

    return null;
  };

  // Use our managed sources state instead of direct query result
  // Only return card PaymentMethods here - bank accounts are handled separately
  const currentSources = React.useMemo(() => {
    // If no recipient or no stripe customer, return empty
    if (!stripeCustomerId || (!recipient && !companyId)) {
      return [];
    }
    return paymentMethods;
  }, [stripeCustomerId, paymentMethods, recipient, companyId]);

  return {
    sources: currentSources,
    bankAccounts: legacyBankAccounts.reduce(
      (acc, bankAccount) => {
        acc[bankAccount.id] = bankAccount;
        return acc;
      },
      {} as Record<string, Stripe.BankAccount>,
    ),
    // Unverified bank accounts awaiting microdeposit verification
    // Only available when enableMicrodepositVerification flag is enabled
    unverifiedBankAccounts: enableMicrodepositVerification
      ? unverifiedBankAccounts
      : EMPTY_UNVERIFIED_BANK_ACCOUNTS,
    defaultSource: defaultPaymentMethod,
    stripeCustomerId,
    makePrimaryMethod,
    currentUpdatingSourceId,
    editModalData,
    setEditModalData,
    handleSave,
    handleDelete,
    handleClose,
    modalRenderer,
    isSourcesLoading: isPaymentMethodLoading,
    isBankAccountsLoading,
    handleVerify,
    // Backwards-compatible aliases for unverified bank account handlers
    // These are conditionally provided based on the feature flag
    handleVerifyUnverifiedBankAccount: enableMicrodepositVerification
      ? handleVerify
      : undefined,
    handleDeleteUnverifiedBankAccount: enableMicrodepositVerification
      ? handleDelete
      : undefined,
    showBankAddedModal,
    setShowBankAddedModal,
    openAddPaymentModal,
    handleAddPaymentMethodSelected,
    enableMicrodepositVerification,
  } as const;
};
